import { isObject, isString, omit } from 'lodash';
import { GET_STUDY_SUMMARY_ENDPOINT } from '../../../common/config/api_endpoints';
import {
  createAPILifecycleActions,
  createAPILifecycleActionTypes,
} from '../../../redux/actions/action_utils';
import createAmplifyAPICallEpic from '../../../redux/epics/utils';
import calculateAdditionalBenefits from '../utils/study_summary_utils';
import STUDY_SUMMARY_TYPES from '../constants/studySummaries';

const GET_STUDY_SUMMARY_ACTION_TYPES = createAPILifecycleActionTypes(
  'GET_STUDY_SUMMARY_ACTION_TYPES',
);
export const getStudySummaryActions = createAPILifecycleActions(
  GET_STUDY_SUMMARY_ACTION_TYPES.NAME,
);

const SUMMARY_PROCESSORS = {
  QRE: summary => calculateAdditionalBenefits(JSON.parse(summary)),
  default: summary => JSON.parse(summary),
};

const processSummaryData = (summaryType, summary) => {
  if (!isString(summaryType) || !isString(summary)) return null;

  try {
    const processor = SUMMARY_PROCESSORS[summaryType] || SUMMARY_PROCESSORS.default;
    return { [summaryType]: processor(summary) };
  } catch (error) {
    return null;
  }
};

// Returns the first survey type found or null.
// Note: If multiple survey types exist, only the first one is used.
// Only 1 surveyType is returned in API response as of now
const extractSurveyType = data => {
  const surveyTypes = Object.keys(data.aggBySurveyType || {});
  return surveyTypes.length > 0 ? surveyTypes[0] : null;
};

const mergeStatusCounts = (existing = {}, incoming = {}) => {
  if (!incoming) return existing;
  return {
    Submitted: (existing.Submitted || 0) + (incoming.Submitted || 0),
    InProgress: (existing.InProgress || 0) + (incoming.InProgress || 0),
    Created: (existing.Created || 0) + (incoming.Created || 0),
  };
};

const mergeSurveyData = (existingData, newData) => {
  if (!existingData) return newData;
  if (!newData) return existingData;

  const surveyType = extractSurveyType(newData);

  return {
    ...(existingData?.surveyTypes || {}),
    // Store individual survey types for detailed calculations
    surveyTypes: {
      ...existingData.surveyTypes,
      ...(surveyType && { [surveyType]: newData }),
    },
    // Merge aggregated status counts for overview - only for survey data
    aggByStatus: mergeStatusCounts(existingData.aggByStatus, newData.aggByStatus),
    // Preserve metadata from latest response
    clientId: newData.clientId || existingData.clientId,
    country: newData.country || existingData.country,
    studyPeriod: newData.studyPeriod || existingData.studyPeriod,
  };
};

const updateStateWithNewData = (state, summaryType, newData) => {
  const existingData = state.data?.[summaryType];

  // Only use survey-specific merging for SURVEYS summary type
  const mergedData =
    summaryType === STUDY_SUMMARY_TYPES.SURVEYS.key
      ? mergeSurveyData(existingData || {}, newData)
      : newData;

  return {
    ...state,
    isFetching: false,
    data: {
      ...(state.data || {}),
      [summaryType]: mergedData,
    },
    error: null,
  };
};

const initialState = {
  isFetching: false,
  data: null,
  error: null,
};

export default function studySummaryReducer(state = initialState, { type, payload }) {
  switch (type) {
    case GET_STUDY_SUMMARY_ACTION_TYPES.BEGIN:
      return {
        ...state,
        isFetching: true,
      };

    case GET_STUDY_SUMMARY_ACTION_TYPES.SUCCESS: {
      if (!isObject(payload)) return state;

      const processedSummary = processSummaryData(payload.summaryType, payload.summary);
      if (!processedSummary) return state;

      const [summaryType, newData] = Object.entries(processedSummary)[0];
      return updateStateWithNewData(state, summaryType, newData);
    }

    case GET_STUDY_SUMMARY_ACTION_TYPES.FAILURE:
      return {
        ...state,
        isFetching: false,
        data: null,
        error: payload,
      };

    case GET_STUDY_SUMMARY_ACTION_TYPES.CLEAR:
      return {
        ...state,
        data: isObject(state.data) && isString(payload) ? omit(state.data, payload) : null,
      };
    default:
      return state;
  }
}

export const getStudySummaryEpic = createAmplifyAPICallEpic({
  actionType: GET_STUDY_SUMMARY_ACTION_TYPES.BEGIN,
  api: GET_STUDY_SUMMARY_ENDPOINT,
  successCallback: response => [getStudySummaryActions.SUCCESS(response.body)],
  errorCallback: error => {
    return [getStudySummaryActions.FAILURE(error)];
  },
});
